import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  RefreshCw,
  Inbox,
  CheckCircle2,
  Clock,
  Clock3,
  AlertTriangle,
  Banknote,
  DollarSign,
  ArrowRight,
  Download,
  FileText,
} from "lucide-react";
import { KpiCard } from "../../components/ui/KpiCard";
import { SectionCard } from "../../components/ui/SectionCard";
import api from "../../services/api";
import { downloadInvoiceExport, exportDateStamp } from "../../services/invoiceExport";
import { useAuth } from "../../contexts/AuthContext";
import {
  DEFAULT_RECEIVED_RANGE_DAYS,
  getDefaultReceivedRange,
  fmtDate,
  fmtAmount,
  fmtAmountShort,
} from "../bureau/utils";
import { InvoiceDrawer } from "../bureau/components/InvoiceDrawer";

const DEFAULT_STATS = {
  total: 0,
  sentToErp: 0,
  notSent: 0,
  overdue: 0,
  late: 0,
  incomplete: 0,
  totalHT: 0,
  totalTTC: 0,
};

const DEFAULT_LISTS = { received: [], processing: [], sent: [], incomplete: [] };

// Each dashboard section lists the 10 most recent matching invoices.
const SECTIONS = [
  { key: "received", filter: { statuses: "received" }, sortBy: "receivedAt", dateField: "receivedAt", link: "status=received" },
  { key: "processing", filter: { statuses: "processing" }, sortBy: "updatedAt", dateField: "updatedAt", link: "status=processing" },
  { key: "sent", filter: { statuses: "sent_to_erp" }, sortBy: "erpSentAt", dateField: "erpSentAt", link: "status=sent_to_erp" },
  { key: "incomplete", filter: { incompleteOnly: "true" }, sortBy: "receivedAt", dateField: "receivedAt", link: "incomplete=true" },
];

function sectionParams(section, range, includePagination = true) {
  const params = new URLSearchParams({
    ...section.filter,
    sortBy: section.sortBy,
    ...(section.key === "incomplete" ? {
      receivedFrom: range.from,
      receivedTo: range.to,
    } : {}),
  });
  if (includePagination) {
    params.set("page", "1");
    params.set("limit", "10");
  }
  return params;
}

function InvoiceListRow({ row, dateField, locale, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition text-left"
    >
      <div className="min-w-0">
        <div className="font-mono text-[12px] font-semibold text-primary truncate">
          {row.invoiceNo || "—"}
        </div>
        <div className="text-[11.5px] text-slate-500 truncate">
          {row.shippingCompanyName || "—"}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-[12.5px] font-semibold text-slate-900 tabular-nums">
          {fmtAmount(row.totalInclVAT, row.currencyCode)}
        </div>
        <div className="text-[11px] text-slate-400">{fmtDate(row[dateField], locale)}</div>
      </div>
    </button>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-2 px-3 py-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" />
      ))}
    </div>
  );
}

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const navigate = useNavigate();
  const { user } = useAuth();
  const canExport = user?.role === "ADMIN" || user?.role === "AGENT";

  const [stats, setStats] = useState(DEFAULT_STATS);
  const [receivedTotal, setReceivedTotal] = useState(null);
  const [countError, setCountError] = useState(false);
  const [lists, setLists] = useState(DEFAULT_LISTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [exportingKey, setExportingKey] = useState(null);
  const kpiRangeRef = useRef(getDefaultReceivedRange());
  const countRequestId = useRef(0);

  const refreshCount = useCallback(() => {
    const requestId = ++countRequestId.current;
    api.get("/bureau-invoices?limit=1")
      .then(({ data }) => {
        if (requestId !== countRequestId.current) return;
        if (!Number.isSafeInteger(data.total) || data.total < 0) {
          throw new Error("Invalid invoice count");
        }
        setReceivedTotal(data.total);
        setCountError(false);
      })
      .catch(() => {
        if (requestId === countRequestId.current) setCountError(true);
      });
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    refreshCount();
    const kpiRange = getDefaultReceivedRange();
    kpiRangeRef.current = kpiRange;
    const kpiParams = new URLSearchParams({
      limit: "1",
      receivedFrom: kpiRange.from,
      receivedTo: kpiRange.to,
    });
    Promise.all([
      api.get(`/bureau-invoices?${kpiParams}`),
      ...SECTIONS.map((section) =>
        api.get(`/bureau-invoices?${sectionParams(section, kpiRange)}`),
      ),
    ])
      .then(([kpiRes, ...sectionRes]) => {
        if (kpiRes.data.stats) setStats(kpiRes.data.stats);
        const next = {};
        SECTIONS.forEach(({ key }, i) => {
          next[key] = sectionRes[i].data.rows ?? [];
        });
        setLists(next);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [refreshCount]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const interval = window.setInterval(refreshCount, 15000);
    const onVisibilityChange = () => {
      if (!document.hidden) refreshCount();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refreshCount]);

  async function exportSection(section) {
    setExportingKey(section.key);
    setError(null);
    try {
      const params = sectionParams(section, kpiRangeRef.current, false);
      params.set("locale", locale.startsWith("en") ? "en" : "fr");
      const name = `factures-${section.key}-${exportDateStamp()}.xlsx`;
      await downloadInvoiceExport(params, name);
    } catch (_) {
      setError(t("invoices.export_error"));
    } finally {
      setExportingKey(null);
    }
  }

  async function exportAllReceived() {
    setExportingKey("all");
    setError(null);
    try {
      const params = new URLSearchParams({ locale: locale.startsWith("en") ? "en" : "fr" });
      await downloadInvoiceExport(params, `factures-toutes-${exportDateStamp()}.xlsx`);
    } catch (_) {
      setError(t("invoices.export_error"));
    } finally {
      setExportingKey(null);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[18px] font-bold text-slate-900">
            {t("dashboard.page_title")}
          </h1>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            {t("dashboard.page_subtitle")}
          </p>
        </div>
        <button
          onClick={load}
          className="h-8 w-8 grid place-items-center text-slate-500 hover:bg-slate-100 rounded-md transition flex-shrink-0"
        >
          <RefreshCw size={14} strokeWidth={1.75} />
        </button>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg px-4 py-3 text-[13px] text-rose-700">
          {error}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">
            {t("dashboard.cumulative_period_label")}
          </p>
          {canExport && (
            <button
              type="button"
              onClick={exportAllReceived}
              disabled={exportingKey !== null}
              title={t("dashboard.export_all")}
              aria-label={t("dashboard.export_all")}
              className="text-slate-500 hover:text-primary disabled:opacity-40"
            >
              <Download size={14} strokeWidth={1.75} />
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 gap-3">
          <KpiCard
            label={t("dashboard.kpi_received")}
            value={receivedTotal ?? "—"}
            icon={Inbox}
            sub={countError ? t("dashboard.count_update_error") : undefined}
          />
        </div>
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-2">
          {t("dashboard.kpi_period_label", { days: DEFAULT_RECEIVED_RANGE_DAYS })}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 gap-3">
          <KpiCard
            label={t("dashboard.kpi_sent_erp")}
            value={stats.sentToErp}
            icon={CheckCircle2}
            iconColor="text-emerald-600"
            sub={`${Math.round((stats.sentToErp / (stats.total || 1)) * 100)}%`}
          />
          <KpiCard
            label={t("dashboard.kpi_not_sent")}
            value={stats.notSent}
            icon={Clock}
            iconColor="text-amber-500"
          />
          <KpiCard
            label={t("dashboard.kpi_overdue")}
            value={stats.overdue}
            icon={AlertTriangle}
            iconColor={stats.overdue > 0 ? "text-rose-600" : "text-slate-400"}
          />
          <KpiCard
            label={t("dashboard.kpi_late")}
            value={stats.late}
            icon={Clock3}
            iconColor={stats.late > 0 ? "text-rose-600" : "text-slate-400"}
          />
          <KpiCard
            label={t("dashboard.kpi_incomplete")}
            value={stats.incomplete}
            icon={FileText}
            iconColor={stats.incomplete > 0 ? "text-amber-600" : "text-slate-400"}
          />
          <KpiCard
            label={t("dashboard.kpi_total_ht")}
            value={fmtAmountShort(stats.totalHT)}
            icon={Banknote}
          />
          <KpiCard
            label={t("dashboard.kpi_total_ttc")}
            value={fmtAmountShort(stats.totalTTC)}
            icon={DollarSign}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {SECTIONS.map((section) => (
          <SectionCard
            key={section.key}
            title={t(`dashboard.section_${section.key}_title`)}
            action={
              <div className="flex items-center gap-2">
                {canExport && (
                  <button
                    type="button"
                    onClick={() => exportSection(section)}
                    disabled={exportingKey !== null}
                    title={t("dashboard.export_section", { section: t(`dashboard.section_${section.key}_title`) })}
                    aria-label={t("dashboard.export_section", { section: t(`dashboard.section_${section.key}_title`) })}
                    className="text-slate-500 hover:text-primary disabled:opacity-40"
                  >
                    <Download size={14} strokeWidth={1.75} />
                  </button>
                )}
                <button
                  onClick={() => navigate(`/dashboard/invoices?${section.link}`)}
                  className="text-[11.5px] font-medium text-primary hover:underline flex items-center gap-1"
                >
                  {t("dashboard.view_all")}
                  <ArrowRight size={12} strokeWidth={2} />
                </button>
              </div>
            }
          >
            {loading ? (
              <ListSkeleton />
            ) : lists[section.key].length === 0 ? (
              <div className="py-8 text-center">
                <FileText size={24} className="mx-auto text-slate-300 mb-2" strokeWidth={1} />
                <p className="text-[12px] text-slate-400">{t("dashboard.section_empty")}</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {lists[section.key].map((row) => (
                  <InvoiceListRow
                    key={row._id}
                    row={row}
                    dateField={section.dateField}
                    locale={locale}
                    onClick={() => setSelectedRow(row)}
                  />
                ))}
              </div>
            )}
          </SectionCard>
        ))}
      </div>

      <InvoiceDrawer row={selectedRow} onClose={() => setSelectedRow(null)} />
    </div>
  );
}
